/**
 * CFS WebMCP tools for AI agents (owner, 14 Oct 2026): Click & collect.
 *
 * Shopify's own agent tools (storefront/webmcp) add to the cart and jump straight to checkout, skipping the
 * basket page, so an agent order always went through as UK delivery: Zapiet's Collect choice, date and time
 * are only made in the basket. These tools let an agent do it the way a shopper does, through Zapiet's own
 * controls on the basket page, so all of Zapiet's rules (minimum order, open dates, time slots) still apply.
 * Nothing is set behind Zapiet's back and no Zapiet internals are called: the tools click its widget.
 *
 *   any page:     open_basket_for_collection
 *   basket page:  get_collection_options, choose_click_and_collect, checkout_from_basket
 *
 * Registers only where the browser offers WebMCP (document.modelContext / navigator.modelContext), so
 * shoppers' browsers do nothing with it. (Pre-selecting Collect on arrival was dropped: Zapiet restores the
 * basket's earlier choice after loading; get_collection_options selects it instead.)
 * If Zapiet changes its widget markup, the selectors below (APP, .checkoutMethod, .Zapiet-*) need a look.
 * They cover both the classic widget and the redesigned one (Zapiet "modern" design, 22 Oct 2026).
 */
const APP = '.cfs-basket--page #storePickupApp';
const ON_BASKET = () => Boolean(document.querySelector('.cfs-basket--page'));

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/** @param {() => any} test @param {number} [timeout] */
async function waitFor(test, timeout = 8000) {
  const end = performance.now() + timeout;
  while (performance.now() < end) {
    const value = test();
    if (value) return value;
    await sleep(150);
  }
  return null;
}

/** @param {string} text @param {object} [data] */
const reply = (text, data) => ({ content: [{ type: 'text', text }], ...(data ? { structuredContent: data } : {}) });
/** @param {string} text */
const fail = (text) => ({ content: [{ type: 'text', text }], isError: true });

const app = () => document.querySelector(APP);
// Zapiet's redesigned widget (owner, 22 Oct 2026) has text tabs ("Store Pickup"); the classic one had image
// buttons the theme labels "Collect"
const collectButton = () =>
  document.querySelector(`${APP} .checkoutMethod[aria-label="Collect"]`) ??
  [...document.querySelectorAll(`${APP} .Zapiet-MethodTabs__Tab`)].find((tab) => /pick ?up|collect/i.test(tab.textContent ?? '')) ??
  null;

async function selectCollect() {
  const zapiet = await waitFor(app, 15000);
  if (!zapiet) return 'The Delivery / Collect choice has not loaded on the basket page (is the basket empty?).';
  const button = await waitFor(collectButton, 8000);
  if (!button) return 'Click & collect is not offered for this basket.';
  if (button.getAttribute('aria-pressed') !== 'true') {
    /** @type {HTMLElement} */ (button).click();
    await waitFor(() => collectButton()?.getAttribute('aria-pressed') === 'true', 6000);
  }
  await waitFor(() => document.querySelector(`${APP} .Zapiet-InputWithPrefix`), 8000);
  return '';
}

/** Text Zapiet shows about the collection (e.g. a minimum order), without the buttons' labels */
function zapietNotices() {
  // The redesigned widget shows "not available" and minimum-order notices in an alert outside the panel
  const boxes = /** @type {HTMLElement[]} */ ([...document.querySelectorAll(`${APP} .checkoutMethodContainer.pickup, ${APP} .Zapiet-Alert`)]);
  if (!boxes.length) return '';
  const text = boxes.map((box) => box.innerText).join(' ').replace(/\s+/g, ' ');
  // One sentence; a full stop inside a price (£11.49) doesn't end it
  const match = text.match(/(?:[^.]|\.(?=\d))*(minimum|not available|unavailable)(?:[^.]|\.(?=\d))*\.?/i);
  return match ? match[0].trim() : '';
}

function collectionPoint() {
  const loc = document.querySelector(
    `${APP} .Zapiet-LocationCard--selected, ${APP} .location.active, ${APP} .Zapiet-LocationCard, ${APP} .location`
  );
  if (!loc) return null;
  const name = loc.querySelector('.name, .Zapiet-LocationCard__Name')?.textContent?.trim() ?? '';
  const address = /** @type {HTMLElement | null} */ (loc.querySelector('.address'))?.innerText.replace(/\s+/g, ' ').trim() ?? '';
  return { name, address };
}

/** "Thu, October 01, 2026" (classic widget) or "Thu, 1 October, 2026" (redesigned) -> "2026-10-01" */
function isoFromLabel(label = '') {
  const rest = label.replace(/^[A-Za-z]+,\s*/, '');
  const dayFirst = rest.match(/^(\d{1,2})\s+([A-Za-z]+),?\s+(\d{4})$/);
  const date = new Date(dayFirst ? `${dayFirst[2]} ${dayFirst[1]}, ${dayFirst[3]}` : rest);
  if (Number.isNaN(date.getTime())) return null;
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

async function openCalendar() {
  const input = /** @type {HTMLElement | null} */ (document.querySelector(`${APP} .Zapiet-InputWithPrefix`));
  if (!input) return null;
  const open = () => document.querySelector(`${APP} .Zapiet-Calendar__Wrapper--open`);
  // Twice: a click just after the calendar closed (or while Zapiet re-renders) can be lost (live test, 22 Oct 2026)
  for (let attempt = 0; attempt < 2 && !open(); attempt++) {
    /** @type {HTMLElement} */ (document.querySelector(`${APP} .Zapiet-InputWithPrefix`) ?? input).click();
    if (await waitFor(open, 2500)) break;
  }
  return open();
}

/**
 * Waits until Zapiet has saved the choice on the basket (cart attributes Checkout-Method, Pickup-Date and
 * Pickup-Time). Zapiet sends a shopper to a pickup checkout only once its save has finished: a Checkout press
 * straight after a new time opened an ordinary delivery checkout (live agent test, 22 Oct 2026).
 * @returns {Promise<boolean>}
 */
async function zapietSaved(timeout = 8000) {
  const end = performance.now() + timeout;
  while (performance.now() < end) {
    const now = selection();
    const isoDate = /** @type {HTMLInputElement | null} */ (document.querySelector(`${APP} #Zapiet-InputCalendar__pickupDate_ISO`))?.value || null;
    try {
      const attrs = (await fetch('/cart.js', { cache: 'no-store' }).then((r) => r.json())).attributes ?? {};
      if (now.method !== 'pickup') return attrs['Checkout-Method'] === now.method || !attrs['Checkout-Method'];
      if (attrs['Checkout-Method'] === 'pickup' && attrs['Pickup-Time'] === now.time && (!isoDate || attrs['Pickup-Date'] === isoDate)) return true;
    } catch {}
    await sleep(500);
  }
  return false;
}

/** The basket's Checkout button, once Zapiet has had a moment to allow it (its checkoutEnabled event lags a click) */
async function checkoutButton(timeout = 8000) {
  const find = () => /** @type {HTMLButtonElement | null} */ (document.querySelector('.cfs-basket--page [data-cfs-checkout]'));
  await waitFor(() => {
    const button = find();
    return button && !button.disabled && button.getAttribute('aria-disabled') !== 'true';
  }, timeout);
  return find();
}

function openDates() {
  return [...document.querySelectorAll(`${APP} .Zapiet-Calendar__Dates button`)]
    .filter((button) => button.getAttribute('aria-disabled') !== 'true')
    .map((button) => ({ button: /** @type {HTMLElement} */ (button), iso: isoFromLabel(button.getAttribute('aria-label') ?? '') }))
    .filter((d) => d.iso);
}

/** @returns {HTMLElement | null} the next-month arrow when it can be used */
function nextMonth() {
  const next = /** @type {HTMLElement | null} */ (document.querySelector(`${APP} .Zapiet-Calendar__Nav[aria-label="Next month"]`));
  return next && next.getAttribute('aria-disabled') !== 'true' ? next : null;
}

/** No calendar: the redesigned widget shows only a notice (e.g. a minimum order) when collection can't be chosen */
function noCalendar() {
  const notice = zapietNotices();
  return fail(notice ? `Click & collect can't be chosen for this basket yet: ${notice}` : 'The collection calendar did not open.');
}

function closeCalendar() {
  document.dispatchEvent(new KeyboardEvent('keydown', { key: 'Escape', bubbles: true }));
}

function selection() {
  let method = null;
  try { method = /** @type {any} */ (window).Zapiet?.getSelectedCheckoutMethod?.() ?? null; } catch {}
  const date = /** @type {HTMLInputElement | null} */ (document.querySelector(`${APP} #Zapiet-InputCalendar__pickup`))?.value || null;
  const time = document.querySelector(`${APP} .Zapiet-Select__Value`)?.textContent?.trim() || null;
  return { method, date, time };
}

async function timeOptions() {
  const select = /** @type {HTMLElement | null} */ (await waitFor(() => document.querySelector(`${APP} .Zapiet-Select__Wrapper`), 6000));
  if (!select) return { select: null, items: [] };
  if (!document.querySelector(`${APP} #Zapiet-TimePicker li, #Zapiet-TimePicker li`)) select.click();
  await waitFor(() => document.querySelector(`${APP} #Zapiet-TimePicker li, #Zapiet-TimePicker li`), 4000);
  const items = /** @type {HTMLElement[]} */ ([...document.querySelectorAll(`${APP} #Zapiet-TimePicker li, #Zapiet-TimePicker li`)]);
  return { select, items };
}

const tools = [
  {
    name: 'open_basket_for_collection',
    annotations: { readOnlyHint: false },
    description:
      'Continental Food Store offers Click & collect from its shop at 96-98 Petersfield Avenue, Harold Hill, Romford RM3 9PH. Use this when the shopper wants to collect in store instead of delivery, or asks for a collection date or time: it MOVES the browser to the basket page. Then call get_collection_options (which selects Click & collect) and choose_click_and_collect, and finish with checkout_from_basket. Do NOT use proceed_to_checkout for collection orders: it skips the basket, so the order would be sent as UK delivery.',
    inputSchema: { type: 'object', properties: {} },
    async execute() {
      setTimeout(() => window.location.assign('/cart'), 50);
      return reply('The browser is moving to the basket page. Once it has loaded, call get_collection_options to select Click & collect and see the dates.');
    },
  },
  {
    name: 'get_collection_options',
    basketOnly: true,
    annotations: { readOnlyHint: false },
    description:
      'On the basket page: selects Click & collect and lists the collection point and the dates that can be chosen (YYYY-MM-DD), plus any condition such as a minimum order for collection. Call choose_click_and_collect next.',
    inputSchema: { type: 'object', properties: {} },
    async execute() {
      const problem = await selectCollect();
      if (problem) return fail(problem);
      const where = collectionPoint();
      const notice = zapietNotices();
      if (!(await openCalendar())) return noCalendar();
      const dates = new Set();
      for (let month = 0; month < 3; month++) {
        for (const d of openDates()) dates.add(d.iso);
        const next = nextMonth();
        if (!next) break;
        next.click();
        await sleep(400);
      }
      closeCalendar();
      const list = [...dates].sort();
      const data = { location: where, dates: list, notice: notice || null, current: selection() };
      const lines = [
        `Click & collect from ${where ? `${where.name}, ${where.address}` : 'the shop'}.`,
        list.length ? `Dates that can be chosen: ${list.join(', ')}.` : 'No collection dates are open at the moment.',
        notice ? `Note from the shop: ${notice}` : '',
        'Times depend on the date: choose_click_and_collect with a date lists them.',
      ].filter(Boolean);
      return reply(lines.join(' '), data);
    },
  },
  {
    name: 'choose_click_and_collect',
    basketOnly: true,
    annotations: { readOnlyHint: false },
    description:
      'On the basket page: chooses Click & collect on the given date (YYYY-MM-DD, from get_collection_options) and, if given, the time (HH:MM, e.g. 10:00). Without a time, or with one that is not offered, it lists the times for that date; call again with one of them. Returns what is now selected. Then call checkout_from_basket.',
    inputSchema: {
      type: 'object',
      required: ['date'],
      properties: {
        date: { type: 'string', description: 'Collection date, YYYY-MM-DD' },
        time: { type: 'string', description: 'Collection time as shown in the list, e.g. 10:00' },
      },
    },
    /** @param {{ date?: string, time?: string }} args */
    async execute(args) {
      const want = String(args?.date ?? '').trim();
      if (!/^\d{4}-\d{2}-\d{2}$/.test(want)) return fail('Give the date as YYYY-MM-DD.');
      const problem = await selectCollect();
      if (problem) return fail(problem);
      if (!(await openCalendar())) return noCalendar();
      let day = null;
      for (let month = 0; month < 3 && !day; month++) {
        day = openDates().find((d) => d.iso === want) ?? null;
        if (day) break;
        const next = nextMonth();
        if (!next) break;
        next.click();
        await sleep(400);
      }
      if (!day) {
        closeCalendar();
        return fail(`${want} cannot be chosen for collection. Call get_collection_options for the open dates.`);
      }
      day.button.click();
      await waitFor(() => selection().date, 6000);
      await sleep(600);
      const { items } = await timeOptions();
      const times = items.map((li) => li.textContent?.trim() ?? '').filter(Boolean);
      const wantTime = String(args?.time ?? '').trim();
      const match = wantTime ? items.find((li) => (li.textContent ?? '').trim().startsWith(wantTime)) : null;
      if (match) {
        match.click();
        await sleep(600);
        await zapietSaved();
        await checkoutButton(); // so "selected" means the basket will let it through
      } else if (items.length) {
        closeCalendar(); // closes the open time list as well
      }
      const now = selection();
      const notice = zapietNotices();
      if (!match && times.length) {
        return reply(`Date set to ${now.date}. Times for that day: ${times.join(', ')}. Call choose_click_and_collect again with the date and one of these times.`, { selected: now, times, notice: notice || null });
      }
      return reply(
        `Click & collect selected: ${now.date ?? want}${now.time ? ` at ${now.time}` : ''}.${notice ? ` Note from the shop: ${notice}` : ''} Next: checkout_from_basket.`,
        { selected: now, notice: notice || null }
      );
    },
  },
  {
    name: 'checkout_from_basket',
    basketOnly: true,
    annotations: { readOnlyHint: false },
    description:
      'On the basket page: presses the basket\'s own Checkout button, so the chosen Click & collect date and time (or delivery) and the shop\'s order checks go with the order. Use this instead of proceed_to_checkout when the shopper chose Click & collect. It MOVES the browser to checkout, where the shopper completes and pays for the order themselves.',
    inputSchema: { type: 'object', properties: {} },
    async execute() {
      const button = await checkoutButton();
      if (!button) return fail('The basket has no Checkout button (is the basket empty?).');
      if (button.disabled || button.getAttribute('aria-disabled') === 'true') {
        const why = /** @type {HTMLElement | null} */ (document.querySelector('.cfs-basket--page .cfs-basket__summary'))?.innerText.replace(/\s+/g, ' ').match(/[^.]*(minimum|add £|choose|select)[^.]*\.?/i)?.[0];
        return fail(`Checkout is not available yet.${why ? ` The basket says: ${why.trim()}` : ''}`);
      }
      const chosen = selection();
      if (!(await zapietSaved())) {
        return fail('The collection choice has not been saved on the basket yet. Wait a moment, then call checkout_from_basket again.');
      }
      // On the label, as a shopper's tap: Bundler takes over a click on the button itself and loses
      // Zapiet's collection details on the way to checkout (cfs-basket.css, live test 22 Oct 2026)
      /** @type {HTMLElement} */ (button.querySelector('[data-cfs-checkout-label]') ?? button).click();
      return reply(`Pressed Checkout (${chosen.method === 'pickup' ? `Click & collect${chosen.date ? ` on ${chosen.date}` : ''}${chosen.time ? ` at ${chosen.time}` : ''}` : 'delivery'}). The browser is moving to checkout; the shopper completes and pays there.`);
    },
  },
];

/** WebMCP, where the browser offers it */
const modelContext = () => /** @type {any} */ (document).modelContext ?? /** @type {any} */ (navigator).modelContext ?? null;

async function register() {
  // On this domain Chrome turns WebMCP on through Shopify's origin-trial script, which can run after this
  // module: wait for it (checked once a second, up to 20 s), as registering early found nothing (dev test).
  let context = modelContext();
  for (let i = 0; i < 20 && !context?.registerTool; i++) {
    await sleep(1000);
    context = modelContext();
  }
  if (!context?.registerTool) return;
  for (const { basketOnly, ...tool } of tools) {
    if (basketOnly && !ON_BASKET()) continue;
    try {
      Promise.resolve(context.registerTool(tool)).catch(() => {});
    } catch {
      // a tool of the same name is already registered
    }
  }
}


register();
